import React, { useEffect } from "react";
import { useRouter } from "next/router";
import { useDispatch } from "react-redux";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation, Trans } from "next-i18next";
import { GetServerSidePropsContext } from "next";
import { loadCurrentpage } from "../../store/slices/app-slice";
import SummaryItem from "../../components/ContentPages/SummaryItem";
import Paragraph from "../../components/ContentPages/Paragraph";
import ParagraphText from "../../components/ContentPages/ParagraphText";
import Border from "../../components/ContentPages/Border";
import { getMetadata, sharedServerSide } from "../../helpers";
import { Locale } from "../../src/types";

export const getServerSideProps = sharedServerSide(
  async ({ locale }: GetServerSidePropsContext) => {
    const currentLocale = locale as Locale;
    const metas = await getMetadata("faq", currentLocale);

    return {
      props: {
        metas,
        ...(await serverSideTranslations(currentLocale, ["common", "faq"])),
      },
    };
  }
);

function FAQ() {
  const router = useRouter();
  const dispatch = useDispatch();
  const { t } = useTranslation("faq");

  useEffect(() => {
    dispatch(loadCurrentpage("FAQ"));
  }, []);

  return (
    <div className="w-full bg-black flex justify-center text-white">
      <div className="lg:w-[1441px] w-full md:pl-[108px] pl-4 md:pr-[108px] pr-4 md:mt-[213px] mt-[113px]">
        <div className="md:w-[632px] w-full display-4 md:display-2 uppercase">
          <Trans
            components={{
              colored: <span className="text-primary" />,
            }}
            i18nKey="faq:title"
          />
        </div>

        <div className="mt-10 md:w-[632px] w-full font-inter font-normal bodyText md:emphasisText">
          <Trans
            components={{
              linked: (
                <a
                  className="border-b hover:border-0 cursor-pointer"
                  href="https://discord.com/invite/ledger"
                  target="_blank"
                  rel="noreferrer"
                />
              ),
            }}
            i18nKey="faq:subtitle"
          />
        </div>

        <div className="mt-20 flex flex-col gap-4 highlightText underline md:w-[632px] w-full">
          <SummaryItem pRef="content1">{t("question1.title")}</SummaryItem>
          <SummaryItem pRef="content2">{t("question2.title")}</SummaryItem>
          <SummaryItem pRef="content3">{t("question3.title")}</SummaryItem>
          <SummaryItem pRef="content4">{t("question4.title")}</SummaryItem>
          <SummaryItem pRef="content5">{t("question5.title")}</SummaryItem>
          <SummaryItem pRef="content6">{t("question6.title")}</SummaryItem>
          <SummaryItem pRef="content7">{t("question7.title")}</SummaryItem>
          <SummaryItem pRef="content8">{t("question8.title")}</SummaryItem>
          <SummaryItem pRef="content9">{t("question9.title")}</SummaryItem>
          <SummaryItem pRef="content10">{t("question10.title")}</SummaryItem>
          <SummaryItem pRef="content11">{t("question11.title")}</SummaryItem>
          <SummaryItem pRef="content12">{t("question12.title")}</SummaryItem>
          <SummaryItem pRef="content13">{t("question13.title")}</SummaryItem>
          <SummaryItem pRef="content14">{t("question14.title")}</SummaryItem>
        </div>

        <div className="mt-20 flex flex-col gap-14 font-['Inter'] md:w-[632px] w-full md:pb-[126px] pb-20">
          <Paragraph pRef="content1" title={t("question1.title")}>
            <ParagraphText>
              <Trans i18nKey="faq:question1.answer" />
            </ParagraphText>
          </Paragraph>

          <Paragraph pRef="content2" title={t("question2.title")}>
            <ParagraphText>
              <Trans i18nKey="faq:question2.answer" />
            </ParagraphText>
          </Paragraph>

          <Paragraph pRef="content3" title={t("question3.title")}>
            <ParagraphText>
              <Trans i18nKey="faq:question3.answer" />
            </ParagraphText>
          </Paragraph>

          <Paragraph pRef="content4" title={t("question4.title")}>
            <ParagraphText>
              <Trans i18nKey="faq:question4.answer" />
            </ParagraphText>
          </Paragraph>

          <Paragraph pRef="content5" title={t("question5.title")}>
            <ParagraphText>
              {t("question5.answer.explanation.title")}
              <div className="pl-[20px]">
                <p>{t("question5.answer.explanation.step1")}</p>
                <p>{t("question5.answer.explanation.step2")}</p>
                <p>{t("question5.answer.explanation.step3")}</p>
                <p>{t("question5.answer.explanation.step4")}</p>
                <p>{t("question5.answer.explanation.step5")}</p>
              </div>
              {t("question5.answer.explanation.conclusion")}
            </ParagraphText>

            <Border>
              <ParagraphText>
                <Trans
                  components={{
                    linked: (
                      <a
                        className="border-b hover:border-0"
                        href="https://support.ledger.com/hc/en-us/articles/4413601680785-Polygon-MATIC-?docs=true"
                        target="_blank"
                        rel="noreferrer"
                      />
                    ),
                  }}
                  i18nKey="faq:question5.answer.note"
                />
              </ParagraphText>
            </Border>
          </Paragraph>

          <Paragraph pRef="content6" title={t("question6.title")}>
            <ParagraphText>
              <Trans
                components={{
                  linked: (
                    <a
                      className="border-b hover:border-0"
                      href="https://www.ledger.com/academy/security/the-safest-way-to-use-metamask"
                      target="_blank"
                      rel="noreferrer"
                    />
                  ),
                }}
                i18nKey="faq:question6.answer"
              />
            </ParagraphText>
          </Paragraph>

          <Paragraph pRef="content7" title={t("question7.title")}>
            <ParagraphText>
              <Trans
                components={{
                  linked: (
                    <a
                      className="border-b hover:border-0"
                      href="https://www.ledger.com/academy/security/the-safest-way-to-use-metamask"
                      target="_blank"
                      rel="noreferrer"
                    />
                  ),
                }}
                i18nKey="faq:question7.answer"
              />
            </ParagraphText>
          </Paragraph>

          <Paragraph pRef="content8" title={t("question8.title")}>
            <ParagraphText>
              <Trans i18nKey="faq:question8.answer" />
            </ParagraphText>
          </Paragraph>

          <Paragraph pRef="content9" title={t("question9.title")}>
            <ParagraphText>
              <Trans i18nKey="faq:question9.answer" />
            </ParagraphText>
          </Paragraph>

          <Paragraph pRef="content10" title={t("question10.title")}>
            <ParagraphText>
              <Trans i18nKey="faq:question10.answer" />
            </ParagraphText>
          </Paragraph>

          <Paragraph pRef="content11" title={t("question11.title")}>
            <ParagraphText>
              <Trans i18nKey="faq:question11.answer" />
            </ParagraphText>
          </Paragraph>

          <Paragraph pRef="content12" title={t("question12.title")}>
            <ParagraphText>
              <Trans i18nKey="faq:question12.answer" />
            </ParagraphText>
          </Paragraph>

          <Paragraph pRef="content13" title={t("question13.title")}>
            <ParagraphText>
              <Trans
                components={{
                  linked: (
                    <button
                      type="button"
                      className="border-b hover:border-black cursor-pointer"
                      onClick={() => router.push("/terms-conditions")}
                    />
                  ),
                }}
                i18nKey="faq:question13.answer"
              />
            </ParagraphText>
          </Paragraph>

          <Paragraph pRef="content14" title={t("question14.title")}>
            <ParagraphText>
              <Trans i18nKey="faq:question14.answer" />
            </ParagraphText>
          </Paragraph>
        </div>
      </div>
    </div>
  );
}

export default FAQ;
